import React from 'react';
import {
  Volume2, Loader2, Check, Play, Pause,
  DollarSign, FileText, TrendingUp, Zap
} from 'lucide-react';
import { VOICE_OPTIONS } from '../utils/voiceList';
import { MODEL_OPTIONS, calculateEstimatedCost } from '../utils/modelConfig';

interface TranscriptionSegment {
  index: number;
  start: string;
  end: string;
  text: string;
  originalText?: string;
}

interface RenderTabProps {
  previewUrl: string;
  processedVideoUrl: string;
  legendaUrl: string;
  transcriptionSegments: TranscriptionSegment[];
  voiceName: string;
  setVoiceName: (v: string) => void;
  modelId: string;
  setModelId: (v: string) => void;
  isSynthesizing: boolean;
  handleSynthesizeAction: () => void;
  // Playback
  playSegment: (videoUrl: string, startStr: string, endStr: string, index: number, type: 'original' | 'processed') => void;
  stopSegment: () => void;
  playingSegment: { index: number; type: 'original' | 'processed' } | null;
  playVoiceSample: (voice: string) => void;
  playingSampleVoice: string | null;
  // Cost
  videoScript: string;
}

export function RenderTab(props: RenderTabProps) {
  const {
    previewUrl, processedVideoUrl, legendaUrl,
    transcriptionSegments, voiceName, setVoiceName,
    modelId, setModelId,
    isSynthesizing, handleSynthesizeAction,
    playSegment, stopSegment, playingSegment,
    playVoiceSample, playingSampleVoice,
    videoScript
  } = props;

  const fullText = transcriptionSegments?.length > 0
    ? transcriptionSegments.map(s => s.text).join(' ')
    : videoScript;
  const charCount = fullText.length;
  const { estimatedCostUSD, estimatedCostBRL, ratePerThousand } = calculateEstimatedCost(charCount, modelId);

  const hasRequirements = (transcriptionSegments?.length > 0 || !!videoScript) && !!voiceName;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', height: '100%', paddingRight: 6 }}>
      {/* Checklist */}
      <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h4 style={{ fontSize: '.85rem', fontWeight: 800, margin: 0, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Zap size={16} /> Checklist de Requisitos
        </h4>
        <div style={{ display: 'flex', gap: 16, fontSize: '.72rem' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: (transcriptionSegments?.length > 0 || videoScript) ? '#22c55e' : '#ef4444' }}>
            {(transcriptionSegments?.length > 0 || videoScript) ? <Check size={12} /> : '○'} Roteiro/Transcrição
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: voiceName ? '#22c55e' : '#ef4444' }}>
            {voiceName ? <Check size={12} /> : '○'} Voz selecionada
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: previewUrl ? '#22c55e' : '#ef4444' }}>
            {previewUrl ? <Check size={12} /> : '○'} Vídeo gravado
          </span>
        </div>
      </div>

      {/* Segment comparison */}
      {transcriptionSegments && transcriptionSegments.length > 0 && (
        <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h4 style={{ fontSize: '.8rem', fontWeight: 700, margin: 0, color: '#a855f7', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FileText size={14} /> Comparação de Segmentos
          </h4>
          <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {transcriptionSegments.map((seg) => (
              <div key={seg.index} style={{ display: 'flex', gap: 8, background: '#0f172a', padding: '8px 10px', borderRadius: 6, border: '1px solid #334155', alignItems: 'center' }}>
                <span style={{ fontSize: '.65rem', color: '#64748b', fontFamily: 'monospace', minWidth: 70 }}>{seg.start} → {seg.end}</span>
                <span style={{ flex: 1, fontSize: '.75rem', color: '#e2e8f0', lineHeight: 1.4 }}>{seg.text}</span>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {previewUrl && (
                    <button
                      onClick={() => {
                        if (playingSegment && playingSegment.index === seg.index && playingSegment.type === 'original') {
                          stopSegment();
                        } else {
                          playSegment(previewUrl, seg.start, seg.end, seg.index, 'original');
                        }
                      }}
                      style={{
                        background: (playingSegment?.index === seg.index && playingSegment?.type === 'original') ? '#ef4444' : '#334155',
                        color: '#f8fafc', border: 'none', borderRadius: 4,
                        padding: '3px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: '.62rem'
                      }}
                      title="Ouvir trecho original"
                    >
                      {(playingSegment?.index === seg.index && playingSegment?.type === 'original') ? <Pause size={10} /> : <Play size={10} />} Orig.
                    </button>
                  )}
                  {processedVideoUrl && (
                    <button
                      onClick={() => {
                        if (playingSegment && playingSegment.index === seg.index && playingSegment.type === 'processed') {
                          stopSegment();
                        } else {
                          playSegment(processedVideoUrl, seg.start, seg.end, seg.index, 'processed');
                        }
                      }}
                      style={{
                        background: (playingSegment?.index === seg.index && playingSegment?.type === 'processed') ? '#ef4444' : '#a855f7',
                        color: '#f8fafc', border: 'none', borderRadius: 4,
                        padding: '3px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: '.62rem'
                      }}
                      title="Ouvir trecho processado"
                    >
                      {(playingSegment?.index === seg.index && playingSegment?.type === 'processed') ? <Pause size={10} /> : <Play size={10} />} IA
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice selection */}
      <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h4 style={{ fontSize: '.8rem', fontWeight: 700, margin: 0, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Volume2 size={14} /> Selecionar Voz da IA
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
          {VOICE_OPTIONS.map((v) => (
            <div
              key={v.id}
              onClick={() => setVoiceName(v.name)}
              style={{
                padding: '10px 12px', borderRadius: 8,
                border: `1.5px solid ${voiceName === v.name ? '#38bdf8' : '#334155'}`,
                background: voiceName === v.name ? 'rgba(56, 189, 248, 0.08)' : '#0f172a',
                cursor: 'pointer', transition: 'all 0.2s',
                display: 'flex', flexDirection: 'column', gap: 4
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '.75rem', fontWeight: 700, color: voiceName === v.name ? '#38bdf8' : '#f8fafc' }}>{v.label}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    playVoiceSample(v.name);
                  }}
                  style={{
                    background: 'transparent', border: 'none', padding: 2, cursor: 'pointer',
                    color: playingSampleVoice === v.name ? '#ef4444' : '#64748b'
                  }}
                  title={`${playingSampleVoice === v.name ? 'Parar' : 'Ouvir'} amostra`}
                >
                  {playingSampleVoice === v.name ? <Pause size={12} /> : <Play size={12} />}
                </button>
              </div>
              <span style={{ fontSize: '.65rem', color: '#94a3b8' }}>{v.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Model + cost */}
      <div style={{ background: '#1e293b', padding: 16, borderRadius: 8, border: '1px solid #334155', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h4 style={{ fontSize: '.8rem', fontWeight: 700, margin: 0, color: '#a855f7', display: 'flex', alignItems: 'center', gap: 6 }}>
          <TrendingUp size={14} /> Modelo e Custos
        </h4>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={modelId}
            onChange={(e) => setModelId(e.target.value)}
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 8,
              border: '1px solid #334155', background: '#0f172a',
              color: '#f8fafc', fontSize: '.78rem'
            }}
          >
            {MODEL_OPTIONS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 16, fontSize: '.72rem', color: '#94a3b8' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <FileText size={12} /> {charCount.toLocaleString()} caracteres
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <DollarSign size={12} /> ~US$ {estimatedCostUSD.toFixed(6)} ({ratePerThousand}/1k chars)
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <DollarSign size={12} /> ~R$ {estimatedCostBRL.toFixed(4)}
          </span>
        </div>
      </div>

      {/* Render button */}
      <button
        onClick={handleSynthesizeAction}
        disabled={isSynthesizing || !hasRequirements}
        style={{
          padding: '14px 20px', borderRadius: 10,
          border: 'none',
          background: hasRequirements ? 'linear-gradient(135deg, #a855f7, #7c3aed)' : '#334155',
          color: '#fff', fontSize: '.85rem', fontWeight: 800,
          cursor: (!hasRequirements || isSynthesizing) ? 'not-allowed' : 'pointer',
          opacity: (!hasRequirements || isSynthesizing) ? 0.6 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all 0.3s',
          boxShadow: hasRequirements ? '0 4px 15px rgba(168, 85, 247, 0.4)' : 'none'
        }}
      >
        {isSynthesizing ? (
          <><Loader2 size={16} className="animate-spin" /> Processando...</>
        ) : (
          <><Zap size={16} /> Renderizar Versão Final</>
        )}
      </button>
    </div>
  );
}
